import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { MessageCircle } from 'lucide-react';

const TYPE_CONFIG = {
  resultat_examen: { icon: '🔬', label: "Résultat d'examen", color: '#6C63FF' },
  annonce: { icon: '📢', label: 'Annonce', color: '#FF6B6B' },
  evenement: { icon: '📅', label: 'Événement', color: '#4ECDC4' },
  rdv: { icon: '🕐', label: 'Rendez-vous', color: '#45B7D1' },
  document: { icon: '📄', label: 'Document', color: '#96CEB4' },
  autre: { icon: '💬', label: 'Autre', color: '#FFEAA7' },
};

export default function ProWall({ childId, currentProId, onOpenMessage }) {
  const [posts, setPosts] = useState([]);
  const [likes, setLikes] = useState({});
  const [comments, setComments] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId && currentProId) {
      fetchPosts();
    }
  }, [childId, currentProId]);

  async function fetchPosts() {
    setLoading(true);

    // Récupérer les posts visibles par ce pro
    const { data: visiblePostIds } = await supabase
      .from('wall_post_visibility')
      .select('post_id')
      .eq('professional_id', currentProId);

    if (!visiblePostIds || visiblePostIds.length === 0) {
      setPosts([]);
      setLoading(false);
      return;
    }

    const ids = visiblePostIds.map(v => v.post_id);

    const { data: postsData } = await supabase
      .from('wall_posts')
      .select('*')
      .eq('child_id', childId)
      .in('id', ids)
      .order('created_at', { ascending: false });

    if (postsData) {
      setPosts(postsData);
      fetchLikesAndComments(postsData.map(p => p.id));
    }
    setLoading(false);
  }

  async function fetchLikesAndComments(ids) {
    if (!ids.length) return;

    const { data: likesData } = await supabase
      .from('wall_post_likes')
      .select('post_id, professional_id')
      .in('post_id', ids);

    const { data: commentsData } = await supabase
      .from('wall_post_comments')
      .select('*, professional_profiles(first_name, last_name)')
      .in('post_id', ids)
      .order('created_at', { ascending: true });

    const likesMap = {};
    const commentsMap = {};
    ids.forEach(id => { likesMap[id] = []; commentsMap[id] = []; });
    likesData?.forEach(l => likesMap[l.post_id]?.push(l.professional_id));
    commentsData?.forEach(c => commentsMap[c.post_id]?.push(c));

    setLikes(likesMap);
    setComments(commentsMap);
  }

  async function handleLike(postId) {
    const alreadyLiked = likes[postId]?.includes(currentProId);
    if (alreadyLiked) {
      await supabase
        .from('wall_post_likes')
        .delete()
        .eq('post_id', postId)
        .eq('professional_id', currentProId);
      setLikes(prev => ({
        ...prev,
        [postId]: prev[postId].filter(id => id !== currentProId),
      }));
    } else {
      await supabase
        .from('wall_post_likes')
        .insert({ post_id: postId, professional_id: currentProId });
      setLikes(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), currentProId],
      }));
    }
  }

  async function handleComment(postId) {
    const text = newComment[postId]?.trim();
    if (!text) return;

    const { data } = await supabase
      .from('wall_post_comments')
      .insert({ post_id: postId, professional_id: currentProId, content: text })
      .select('*, professional_profiles(first_name, last_name)')
      .single();

    if (data) {
      setComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), data],
      }));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
    }
  }

  function toggleComments(postId) {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  if (loading) return <p style={{ color: '#888', fontSize: 14, padding: 16 }}>Chargement…</p>;

  if (posts.length === 0) return (
    <div style={{ textAlign: 'center', padding: '48px 0', color: '#aaa' }}>
      <div style={{ fontSize: 40 }}>📋</div>
      <p style={{ marginTop: 8 }}>Aucune publication partagée avec vous pour cet enfant.</p>
    </div>
  );

  return (
    <div style={{ padding: '16px 0' }}>
      {posts.map(post => {
        const cfg = TYPE_CONFIG[post.type] || TYPE_CONFIG.autre;
        const postLikes = likes[post.id] || [];
        const postComments = comments[post.id] || [];
        const alreadyLiked = postLikes.includes(currentProId);
        const isExpanded = expandedComments[post.id];

        return (
          <div key={post.id} style={{
            background: '#fff', borderRadius: 16, marginBottom: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden',
          }}>
            {/* Barre colorée */}
            <div style={{ height: 4, background: cfg.color }} />

            <div style={{ padding: 16 }}>
              {/* Type + date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{
                  background: cfg.color + '20', color: cfg.color,
                  borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600,
                }}>
                  {cfg.icon} {cfg.label}
                </span>
                <span style={{ fontSize: 12, color: '#aaa' }}>{formatDate(post.created_at)}</span>
              </div>

              {/* Titre */}
              <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{post.title}</h3>

              {/* Contenu */}
              {post.content && (
                <p style={{ margin: '0 0 10px', fontSize: 14, color: '#555', lineHeight: 1.5 }}>{post.content}</p>
              )}

              {/* Document */}
              {post.document_url && (
                <a href={post.document_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6C63FF', fontWeight: 600 }}>
                  📎 Voir le document
                </a>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
                {/* Like */}
                <button
                  onClick={() => handleLike(post.id)}
                  style={{
                    background: alreadyLiked ? '#FF6B6B15' : 'none',
                    border: `1px solid ${alreadyLiked ? '#FF6B6B' : '#e0e0e0'}`,
                    borderRadius: 20, padding: '5px 12px', fontSize: 13,
                    color: alreadyLiked ? '#FF6B6B' : '#888', cursor: 'pointer',
                    fontWeight: alreadyLiked ? 700 : 400,
                  }}
                >
                  ❤️ {postLikes.length} {postLikes.length > 1 ? 'likes' : 'like'}
                </button>

                {/* Commenter */}
                <button
                  onClick={() => toggleComments(post.id)}
                  style={{
                    background: isExpanded ? '#6C63FF15' : 'none',
                    border: `1px solid ${isExpanded ? '#6C63FF' : '#e0e0e0'}`,
                    borderRadius: 20, padding: '5px 12px', fontSize: 13,
                    color: isExpanded ? '#6C63FF' : '#888', cursor: 'pointer',
                    fontWeight: isExpanded ? 700 : 400,
                  }}
                >
                  💬 {postComments.length} commentaire{postComments.length > 1 ? 's' : ''}
                </button>

                {/* Message privé */}
                {onOpenMessage && (
                  <button
                    onClick={() => onOpenMessage(post)}
                    style={{
                      background: 'none', border: '1px solid #e0e0e0',
                      borderRadius: 20, padding: '5px 12px', fontSize: 13,
                      color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <MessageCircle size={13} /> Message privé
                  </button>
                )}
              </div>

              {/* Zone commentaires */}
              {isExpanded && (
                <div style={{ marginTop: 12 }}>
                  {postComments.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#aaa', marginBottom: 8 }}>Aucun commentaire pour l'instant.</p>
                  ) : (
                    postComments.map(c => (
                      <div key={c.id} style={{
                        background: '#f8f8ff', borderRadius: 10,
                        padding: '10px 12px', marginBottom: 8,
                      }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                          {c.professional_profiles?.first_name} {c.professional_profiles?.last_name}
                        </div>
                        <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{c.content}</div>
                        <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>{formatDate(c.created_at)}</div>
                      </div>
                    ))
                  )}

                  {/* Saisie commentaire */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input
                      value={newComment[post.id] || ''}
                      onChange={e => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && handleComment(post.id)}
                      placeholder="Écrire un commentaire…"
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 20,
                        border: '1.5px solid #e0e0e0', fontSize: 13,
                        outline: 'none', fontFamily: 'inherit',
                      }}
                    />
                    <button
                      onClick={() => handleComment(post.id)}
                      style={{
                        background: '#6C63FF', color: '#fff', border: 'none',
                        borderRadius: 20, padding: '8px 16px', fontSize: 13,
                        fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Envoyer
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}